import { useState } from 'react'

import { LedgerBadge, type LedgerBadgeTone } from '@/components/ui/LedgerTable'
import { UnderlineInput } from '@/components/ui/underline-input'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'

export type HistoryEventKind =
  | 'note'
  | 'note_edited'
  | 'note_deleted'
  | 'follow_up'
  | 'service'
  | 'status'
  | 'details_changed'

/** Where a history row leads: back into a thread, or to a service's page. */
export type HistoryLink =
  | { kind: 'thread'; threadKey: string }
  | {
      kind: 'service'
      serviceType: 'Callout' | 'Session' | 'Job'
      routeId: string
    }

/** One thing that happened to this customer, ready for display. */
export interface CustomerHistoryEvent {
  id: string
  at: string
  kind: HistoryEventKind
  /** Short badge text: "Note", "Follow-up", "Job", "Status", … */
  label: string
  /** What happened, in a sentence. */
  description: string
  actorName?: string
  /** The entity this event is about; rows with one open it on click. */
  link?: HistoryLink
}

const kindTone: Record<HistoryEventKind, LedgerBadgeTone> = {
  note: 'amber',
  note_edited: 'amber',
  note_deleted: 'amber',
  follow_up: 'violet',
  service: 'blue',
  status: 'slate',
  details_changed: 'teal',
}

/**
 * The audit view: everything that happened, strictly newest-first.
 *
 * The conversation tab reorders by activity so the live conversation stays at
 * hand; this tab is its counterweight — nothing is grouped, bumped, or
 * collapsed, so "what happened when" always has one honest answer. Each row is
 * also the way back in: clicking one opens the thread or service it records.
 */
/** How many rows show before "Show earlier" — keeps old customers' DOM small. */
const PAGE_SIZE = 50

export function CustomerHistory({
  events,
  onOpenLink,
}: {
  events: CustomerHistoryEvent[]
  onOpenLink?: (link: HistoryLink) => void
}) {
  const [query, setQuery] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const ordered = [...events].sort((a, b) => b.at.localeCompare(a.at))

  const trimmedQuery = query.trim().toLowerCase()
  const filtered = trimmedQuery
    ? ordered.filter((event) =>
        `${event.label} ${event.description} ${event.actorName ?? ''}`
          .toLowerCase()
          .includes(trimmedQuery),
      )
    : ordered
  const visible = filtered.slice(0, visibleCount)
  const hiddenCount = filtered.length - visible.length

  if (ordered.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        Nothing recorded for this customer yet.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <UnderlineInput
        value={query}
        placeholder="Search history…"
        aria-label="Search history"
        onChange={(event) => {
          setQuery(event.target.value)
          // A new search starts from the top again.
          setVisibleCount(PAGE_SIZE)
        }}
      />
      {filtered.length === 0 ? (
        <p className="py-2 text-xs text-slate-500">
          Nothing matches “{query.trim()}”.
        </p>
      ) : null}
    <ol className="space-y-0">
      {visible.map((event) => {
        const openable = Boolean(event.link && onOpenLink)
        return (
          <li
            key={event.id}
            className="grid grid-cols-[9.5rem_1fr] gap-3 border-b border-slate-100 py-2 max-sm:grid-cols-1 max-sm:gap-1"
          >
            <div className="font-mono text-[11px] leading-5 text-slate-400">
              {formatRelativeDateTime(event.at)}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-1.5">
                <LedgerBadge tone={kindTone[event.kind]}>
                  {event.label}
                </LedgerBadge>
                {openable ? (
                  <button
                    type="button"
                    className="min-w-0 cursor-pointer text-left text-xs leading-5 text-blue-700 hover:underline"
                    title="Open"
                    onClick={() => onOpenLink!(event.link!)}
                  >
                    {event.description}
                    <span aria-hidden="true" className="ml-1 text-slate-400">
                      →
                    </span>
                  </button>
                ) : (
                  <span className="min-w-0 text-xs leading-5 text-slate-800">
                    {event.description}
                  </span>
                )}
              </div>
              {event.actorName ? (
                <div className="mt-0.5 text-[11px] text-slate-400">
                  {event.actorName}
                </div>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
      {hiddenCount > 0 ? (
        <button
          type="button"
          className="cursor-pointer text-xs text-blue-700 hover:underline"
          onClick={() => setVisibleCount((count) => count + PAGE_SIZE * 2)}
        >
          Show earlier ({hiddenCount} more)
        </button>
      ) : null}
    </div>
  )
}
